import { useState, useEffect } from 'react'

/**
 * Resolves the best available project or developer logo via sequential HEAD requests.
 *
 * Priority order:
 *   1. {projectDataUrl}website/assets/project_logo.svg
 *   2. {projectDataUrl}website/assets/project_logo.png
 *   3. {projectDataUrl}website/assets/developer_logo.svg
 *   4. {projectDataUrl}website/assets/developer_logo.png
 *   5. Fallback: inline APLUS geometric mark (data URI)
 *
 * Returns `logoSrc: null` until resolution completes — the consumer should render
 * nothing until `logoReady` is true to avoid a fallback→logo flash.
 *
 * @param {string|null} projectDataUrl - Base asset URL from window.bimCadaplusData.projectDataUrl
 * @returns {{ logoSrc: string|null, logoReady: boolean }}
 */
export function useProjectLogo(projectDataUrl) {
  const [logoSrc, setLogoSrc] = useState(null)

  useEffect(() => {
    if (!projectDataUrl) return

    let cancelled = false

    const base = projectDataUrl.endsWith('/') ? projectDataUrl : `${projectDataUrl}/`

    const candidates = [
      `${base}website/assets/project_logo.svg`,
      `${base}website/assets/project_logo.png`,
      `${base}website/assets/developer_logo.svg`,
      `${base}website/assets/developer_logo.png`,
    ]

    function isHtmlFallbackResponse(res) {
      const contentType = (res.headers.get('content-type') ?? '').toLowerCase()
      return contentType.includes('text/html')
    }

    async function resolveLogo() {
      for (const url of candidates) {
        try {
          // Use GET (not HEAD) so we can reliably detect dev-server HTML fallbacks.
          const res = await fetch(url, { method: 'GET', cache: 'no-store' })
          if (res.ok && !isHtmlFallbackResponse(res)) {
            if (!cancelled) setLogoSrc(url)
            return
          }
        } catch {
          // Network error — try next candidate
        }
      }
      // Nothing found — use inline fallback mark
      if (!cancelled) setLogoSrc(APLUS_MARK_SVG)
    }

    resolveLogo()

    return () => {
      cancelled = true
    }
  }, [projectDataUrl])

  return { logoSrc, logoReady: logoSrc !== null }
}

/**
 * Inline SVG fallback — APLUS geometric mark.
 * Displayed when no project or developer logo asset is found at projectDataUrl.
 */
const APLUS_MARK_SVG = `data:image/svg+xml,${encodeURIComponent(
  '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">' +
    '<path fill="#CA5758" d="M500 649 L500 952 659 873 500 649zM834 797 L1000 707 500 0 500 324 834 797z"/>' +
    '<path fill="#F16664" d="M166 797 L0 707 500 0 500 324 166 797zM500 649 L500 952 341 873 500 649z"/>' +
  '</svg>'
)}`
